import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { UserRole } from '../../models/common/user-role.type';
import { BaseSessionService } from '../services/base-session.service';
import { loginRouteForPortal, portalForRole, portalHomeForRole } from './portal-routes';

/**
 * Guards a portal behind a valid session AND an allowed role.
 * - No / expired session → the portal's own login (with redirectUrl).
 * - Wrong role → bounced to that user's own portal home.
 * Composes session + role checks, so it replaces `authGuard` on portal routes.
 */
export function roleGuard(allowed: readonly UserRole[]): CanActivateFn {
  return (_route, state) => {
    const session = inject(BaseSessionService);
    const router = inject(Router);

    if (!session.isTokenValid()) {
      // Clear session storage when token is invalid/expired
      session.logout();

      // Send unauthenticated users to the login for the portal they were reaching.
      const portal = portalForRole(allowed[0]);
      return router.createUrlTree([loginRouteForPortal(portal)], {
        queryParams: { redirectUrl: state.url }
      });
    }

    const role = session.getCurrentUser()?.role;
    if (role && allowed.includes(role as UserRole)) {
      return true;
    }

    // Authenticated but wrong portal — redirect to where this role belongs.
    return router.createUrlTree([portalHomeForRole(role)]);
  };
}
